// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: Settings > Assistant > Proactive — what the engine may bring up on
// its own, when it may speak, and how much of the user's plan it may spend
// deciding. Switches save at once; the hours and the limits are debounced so a
// slider drag or a half-typed time is one request. Each trigger shows how the
// last two weeks went, and the unwanted briefs can be saved for the eval harness.

import kitStyles from '@app/features/app/components/dialogs/shared/SettingsKit.module.css';
import {SettingsLinkRow, SettingsRow} from '@app/features/app/components/dialogs/shared/SettingsRow';
import {SettingsSection} from '@app/features/app/components/dialogs/shared/SettingsSection';
import AssistantSuggestions from '@app/features/konvo_assistant/state/AssistantSuggestions';
import KonvoConnections, {
	type KonvoProactiveSettingsPatch,
	type KonvoProactiveTriggerStats,
} from '@app/features/konvo_connections/state/KonvoConnections';
import {
	clampProactiveLimit,
	isClockTime,
	orderProactiveTriggers,
	PROACTIVE_LIMITS,
	type ProactiveLimitKey,
	proactiveFeedbackFileName,
	summarizeProactiveStats,
} from '@app/features/konvo_connections/state/KonvoProactive';
import MailSummaries from '@app/features/konvo_mail/state/MailSummary';
import {downloadTextFile} from '@app/features/platform/utils/DownloadFile';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {Input} from '@app/features/ui/components/form/FormInput';
import {Switch} from '@app/features/ui/components/form/FormSwitch';
import {Slider} from '@app/features/ui/components/Slider';
import {
	BellIcon,
	CalendarBlankIcon,
	ChatCircleDotsIcon,
	ClockIcon,
	DownloadSimpleIcon,
	EnvelopeSimpleIcon,
	LightbulbFilamentIcon,
	SparkleIcon,
} from '@app/features/ui/icons/Icons';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import styles from '@app/features/user/components/modals/tabs/KonvoAssistantProactiveSection.module.css';
import {KonvoAssistantUsageSection} from '@app/features/user/components/modals/tabs/KonvoAssistantUsageSection';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {IconTerminal2} from '@tabler/icons-react';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useEffect, useRef, useState} from 'react';

const SAVE_DELAY_MS = 700;

const FAILED_DESCRIPTOR = msg({
	message: 'That did not save. Try again.',
	comment: 'Toast when an assistant setting fails.',
});
const MASTER_DESCRIPTOR = msg({
	message: 'Let Konvo bring things to you',
	comment: 'Master switch of the proactive assistant in Settings > Assistant.',
});
const MASTER_DESCRIPTION_DESCRIPTOR = msg({
	message:
		'Konvo keeps an eye on your calendar, mail and tasks and posts a short brief or nudge in your Assistant DM when something needs you. It never sends or changes anything on its own.',
	comment: 'Description under the proactive assistant master switch.',
});
const CHAT_SUGGESTIONS_DESCRIPTOR = msg({
	message: 'Suggest actions from what people say',
	comment: 'Assistant settings toggle for the suggestion row above the message box.',
});
const MAIL_SUMMARIES_DESCRIPTOR = msg({
	message: 'Summarise emails',
	comment: 'Assistant settings toggle for the Konvo summary card shown when an email is opened.',
});
const MAIL_SUMMARIES_DESCRIPTION_DESCRIPTOR = msg({
	message:
		'When you open an email, the assistant says in a sentence what it needs from you and offers a drafted reply, a time to hold or a task. Hide a single summary with its close button. Uses your assistant plan. Saved on this device.',
	comment: 'Description under the email summaries toggle.',
});
const CHAT_SUGGESTIONS_DESCRIPTION_DESCRIPTOR = msg({
	message:
		'When a message you are typing hands someone work, Konvo offers the next step above the message box: a ClickUp task, a reminder, an event or time blocked in your calendar. The same goes for asks in messages you receive: Konvo looks as they arrive, so the step is ready when you open the chat, and a question about when to meet gets times from your calendar. Nothing happens until you tap it, and Undo takes it back. Uses your assistant plan.',
	comment:
		'Description under the chat suggestions toggle. It covers suggestions for the draft being typed and for received messages, in Konvo chats and WhatsApp.',
});
const FROM_DESCRIPTOR = msg({message: 'From', comment: 'Start of the proactive assistant active hours.'});
const UNTIL_DESCRIPTOR = msg({message: 'Until', comment: 'End of the proactive assistant active hours.'});
const WEEKENDS_DESCRIPTOR = msg({
	message: 'Weekends too',
	comment: 'Proactive assistant switch: also speak up on Saturday and Sunday.',
});
const WEEKENDS_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Off means Konvo stays quiet on Saturdays and Sundays.',
	comment: 'Description under the proactive assistant weekends switch.',
});
const ITEMS_LIMIT_DESCRIPTOR = msg({
	message: 'Things surfaced per day',
	comment: 'Label of the slider for how many briefs and nudges the assistant may post per day.',
});
const WRAP_UP_MINUTES_DESCRIPTOR = msg({
	message: 'Minutes before your day ends',
	comment: 'Proactive assistant settings, under the end-of-day wrap-up switch: slider for when the wrap-up comes.',
});
const WRAP_UP_AGENTS_DESCRIPTOR = msg({
	message: 'Offer to start agents',
	comment: 'Proactive assistant settings, under the end-of-day wrap-up switch.',
});
const WRAP_UP_AGENTS_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Suggest coding agents that can work tonight on your own machines. Nothing starts until you tap Start.',
	comment: 'Description of the proactive assistant "Offer to start agents" switch.',
});
const DECISIONS_LIMIT_DESCRIPTOR = msg({
	message: 'Checks per day',
	comment: 'Label of the slider for how many assistant calls the proactive engine may make per day.',
});
const STATS_COUNTS_DESCRIPTOR = msg({
	message: 'Used {used} of {decided} in the last two weeks',
	comment:
		'Line under a proactive assistant trigger. {used} and {decided} are numbers: briefs acted on out of those the user saw.',
});
const STATS_UNWANTED_DESCRIPTOR = msg({
	message: '{count} unwanted',
	comment: 'Appended to the proactive trigger stats line after a dot. {count} is how many the user marked unwanted.',
});
const STATS_PENDING_DESCRIPTOR = msg({
	message: '{count} surfaced in the last two weeks',
	comment:
		'Line under a proactive assistant trigger when none of its briefs has been acted on yet. {count} is a number.',
});
const STATS_EMPTY_DESCRIPTOR = msg({
	message: 'Nothing surfaced yet',
	comment: 'Line under a proactive assistant trigger that has not brought anything up in the last two weeks.',
});
const SELECTIVE_DESCRIPTOR = msg({
	message: 'Being more selective',
	comment: 'Badge on a proactive assistant trigger whose recent briefs mostly went unused.',
});
const SELECTIVE_REASON_DESCRIPTOR = msg({
	message: 'Several recent ones went unused, so Konvo only surfaces clearer cases',
	comment: 'Tooltip of the "Being more selective" badge on a proactive assistant trigger.',
});
const STRICT_DESCRIPTOR = msg({
	message: 'Only the clearest cases',
	comment: 'Badge on a proactive assistant trigger the user marked unwanted more than once.',
});
const STRICT_REASON_DESCRIPTOR = msg({
	message: 'You marked some of these as unwanted',
	comment: 'Tooltip of the "Only the clearest cases" badge on a proactive assistant trigger.',
});
const EXPORT_FAILED_DESCRIPTOR = msg({
	message: 'Could not download the file. Try again.',
	comment: 'Toast when downloading the unwanted proactive briefs fails.',
});

type Drafts = Partial<Pick<KonvoProactiveSettingsPatch, ProactiveLimitKey | 'active_from' | 'active_until'>>;

function LimitSlider({
	label,
	limit,
	value,
	disabled,
	onChange,
	children,
}: {
	label: string;
	limit: ProactiveLimitKey;
	value: number;
	disabled: boolean;
	onChange: (value: number) => void;
	children: React.ReactNode;
}) {
	const {min, max, fallback} = PROACTIVE_LIMITS[limit];
	return (
		<SettingsRow
			icon={
				limit === 'max_items_per_day' ? (
					<BellIcon size={20} />
				) : limit === 'wrap_up_minutes' ? (
					<ClockIcon size={20} />
				) : (
					<LightbulbFilamentIcon size={20} />
				)
			}
			title={label}
			description={children}
			disabled={disabled}
			control={({labelId}) => (
				<span className={kitStyles.rowSlider}>
					<Slider
						value={value}
						defaultValue={value}
						factoryDefaultValue={fallback}
						minValue={min}
						maxValue={max}
						step={1}
						disabled={disabled}
						ariaLabelledBy={labelId}
						onValueRender={(next) => Math.round(next)}
						onValueChange={(next) => onChange(clampProactiveLimit(limit, next))}
					/>
					<span className={kitStyles.rowValue} aria-hidden="true">
						{value}
					</span>
				</span>
			)}
			data-flx={`user.konvo-assistant-tab.proactive.${limit}`}
		/>
	);
}

export const KonvoAssistantProactiveSection = observer(() => {
	const {i18n} = useLingui();
	const settings = KonvoConnections.proactiveSettings;
	const engine = KonvoConnections.engine;
	const timezone = KonvoConnections.assistantSettings?.effective_timezone ?? null;
	const [drafts, setDrafts] = useState<Drafts>({});
	const pending = useRef<KonvoProactiveSettingsPatch>({});
	const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		void KonvoConnections.refreshEngine().catch(() => undefined);
	}, []);

	const save = useCallback(
		(patch: KonvoProactiveSettingsPatch) => {
			void KonvoConnections.updateProactiveSettings(patch).catch(() => ToastCommands.error(i18n._(FAILED_DESCRIPTOR)));
		},
		[i18n],
	);

	/* The store takes the new values optimistically, so the drafts can go as soon as they are sent. */
	const flush = useCallback(() => {
		timer.current = null;
		const patch = pending.current;
		pending.current = {};
		if (Object.keys(patch).length === 0) return;
		setDrafts((current) => {
			const next = {...current};
			for (const key of Object.keys(patch) as Array<keyof Drafts>) delete next[key];
			return next;
		});
		save(patch);
	}, [save]);

	const saveSoon = (patch: Drafts) => {
		pending.current = {...pending.current, ...patch};
		if (timer.current) clearTimeout(timer.current);
		timer.current = setTimeout(flush, SAVE_DELAY_MS);
	};

	const flushRef = useRef(flush);
	flushRef.current = flush;
	useEffect(
		() => () => {
			if (!timer.current) return;
			clearTimeout(timer.current);
			flushRef.current();
		},
		[],
	);

	const chatSuggestions = (
		<Switch
			label={i18n._(CHAT_SUGGESTIONS_DESCRIPTOR)}
			description={i18n._(CHAT_SUGGESTIONS_DESCRIPTION_DESCRIPTOR)}
			value={AssistantSuggestions.enabled}
			onChange={AssistantSuggestions.setEnabled}
			icon={<ChatCircleDotsIcon size={20} />}
			data-flx="user.konvo-assistant-tab.chat-suggestions"
		/>
	);
	const mailSummaries = (
		<Switch
			label={i18n._(MAIL_SUMMARIES_DESCRIPTOR)}
			description={i18n._(MAIL_SUMMARIES_DESCRIPTION_DESCRIPTOR)}
			value={MailSummaries.enabled}
			onChange={MailSummaries.setEnabled}
			icon={<EnvelopeSimpleIcon size={20} />}
			data-flx="user.konvo-assistant-tab.mail-summaries"
		/>
	);

	const section = (children: React.ReactNode) => (
		<SettingsSection
			id="konvo-assistant-proactive"
			title={<Trans>Proactive</Trans>}
			description={<Trans>What Konvo may bring up without being asked, and when.</Trans>}
			data-flx="user.konvo-assistant-tab.section.proactive"
		>
			{children}
		</SettingsSection>
	);

	// Until the server reports the engine, only the on-device suggestions switch applies.
	if (!settings)
		return section(
			<>
				{chatSuggestions}
				{mailSummaries}
			</>,
		);

	const off = !settings.enabled;
	const activeFrom = drafts.active_from ?? settings.active_from;
	const activeUntil = drafts.active_until ?? settings.active_until;
	const maxItems = drafts.max_items_per_day ?? settings.max_items_per_day;
	const maxDecisions = drafts.max_decisions_per_day ?? settings.max_decisions_per_day;
	const wrapUpMinutes = drafts.wrap_up_minutes ?? settings.wrap_up_minutes ?? PROACTIVE_LIMITS.wrap_up_minutes.fallback;
	const triggers = orderProactiveTriggers(engine?.triggers ?? []).filter((trigger) => trigger.kind !== 'chat_message');
	const today = engine?.today;
	const setTime = (key: 'active_from' | 'active_until', value: string) => {
		setDrafts((current) => ({...current, [key]: value}));
		if (isClockTime(value)) saveSoon({[key]: value});
	};
	const setLimit = (key: ProactiveLimitKey, value: number) => {
		if (value === (drafts[key] ?? settings[key])) return;
		setDrafts((current) => ({...current, [key]: value}));
		saveSoon({[key]: value});
	};

	return section(
		<>
			<Switch
				label={i18n._(MASTER_DESCRIPTOR)}
				description={i18n._(MASTER_DESCRIPTION_DESCRIPTOR)}
				value={settings.enabled}
				onChange={(enabled) => save({enabled})}
				icon={<SparkleIcon size={20} />}
				data-flx="user.konvo-assistant-tab.proactive.enabled"
			/>

			<div className={styles.group}>
				<h3 className={styles.groupTitle}>
					<Trans>What to bring up</Trans>
				</h3>
				{triggers.map((trigger) => (
					<div key={trigger.kind} className={styles.trigger} data-settings-row="">
						<Switch
							label={trigger.label}
							description={trigger.description}
							value={settings.triggers[trigger.kind] ?? trigger.enabled}
							disabled={off}
							onChange={(enabled) => save({triggers: {[trigger.kind]: enabled}})}
							icon={<LightbulbFilamentIcon size={20} />}
							data-flx={`user.konvo-assistant-tab.proactive.trigger.${trigger.kind}`}
						/>
						{engine?.stats && <TriggerStats kind={trigger.kind} stats={engine.stats[trigger.kind]} />}
						{trigger.kind === 'wrap_up' && settings.triggers.wrap_up !== false && settings.wrap_up_minutes != null && (
							<>
								<LimitSlider
									label={i18n._(WRAP_UP_MINUTES_DESCRIPTOR)}
									limit="wrap_up_minutes"
									value={wrapUpMinutes}
									disabled={off}
									onChange={(value) => setLimit('wrap_up_minutes', value)}
								>
									<Trans>How many minutes before the end of your active hours the wrap-up comes.</Trans>
								</LimitSlider>
								<Switch
									label={i18n._(WRAP_UP_AGENTS_DESCRIPTOR)}
									description={i18n._(WRAP_UP_AGENTS_DESCRIPTION_DESCRIPTOR)}
									value={settings.wrap_up_agents !== false}
									disabled={off}
									onChange={(on) => save({wrap_up_agents: on})}
									icon={<IconTerminal2 size={20} stroke={1.75} />}
									data-flx="user.konvo-assistant-tab.proactive.wrap-up-agents"
								/>
							</>
						)}
					</div>
				))}
				{chatSuggestions}
				{mailSummaries}
			</div>

			<div className={styles.group}>
				<h3 className={styles.groupTitle}>
					<Trans>Active hours</Trans>
				</h3>
				<div className={styles.hours}>
					<Input
						type="time"
						label={i18n._(FROM_DESCRIPTOR)}
						value={activeFrom}
						disabled={off}
						onChange={(event) => setTime('active_from', event.target.value)}
						data-flx="user.konvo-assistant-tab.proactive.active-from"
					/>
					<Input
						type="time"
						label={i18n._(UNTIL_DESCRIPTOR)}
						value={activeUntil}
						disabled={off}
						onChange={(event) => setTime('active_until', event.target.value)}
						data-flx="user.konvo-assistant-tab.proactive.active-until"
					/>
				</div>
				<p className={styles.note}>
					{timezone ? (
						<Trans>Konvo stays quiet outside these hours ({timezone}).</Trans>
					) : (
						<Trans>Konvo stays quiet outside these hours.</Trans>
					)}
				</p>
				<Switch
					label={i18n._(WEEKENDS_DESCRIPTOR)}
					description={i18n._(WEEKENDS_DESCRIPTION_DESCRIPTOR)}
					value={settings.weekends}
					disabled={off}
					onChange={(weekends) => save({weekends})}
					icon={<CalendarBlankIcon size={20} />}
					data-flx="user.konvo-assistant-tab.proactive.weekends"
				/>
			</div>

			<div className={styles.group}>
				<h3 className={styles.groupTitle}>
					<Trans>Daily limits</Trans>
				</h3>
				<LimitSlider
					label={i18n._(ITEMS_LIMIT_DESCRIPTOR)}
					limit="max_items_per_day"
					value={maxItems}
					disabled={off}
					onChange={(value) => setLimit('max_items_per_day', value)}
				>
					<Trans>The most briefs and nudges Konvo posts in a day. Anything past this waits until tomorrow.</Trans>
				</LimitSlider>
				<LimitSlider
					label={i18n._(DECISIONS_LIMIT_DESCRIPTOR)}
					limit="max_decisions_per_day"
					value={maxDecisions}
					disabled={off}
					onChange={(value) => setLimit('max_decisions_per_day', value)}
				>
					<Trans>
						Each check is one assistant call on your own plan, made to decide whether something is worth your attention.
						Fewer checks means Konvo notices less.
					</Trans>
				</LimitSlider>
				{today && <TodayLine today={today} />}
			</div>

			<KonvoAssistantUsageSection />
			<FeedbackExport />
		</>,
	);
});

function TriggerStats({kind, stats}: {kind: string; stats: KonvoProactiveTriggerStats | undefined}) {
	const {i18n} = useLingui();
	const summary = summarizeProactiveStats(stats);
	const text =
		summary.kind === 'counts'
			? summary.unwanted > 0
				? `${i18n._(STATS_COUNTS_DESCRIPTOR, summary)} · ${i18n._(STATS_UNWANTED_DESCRIPTOR, {count: summary.unwanted})}`
				: i18n._(STATS_COUNTS_DESCRIPTOR, summary)
			: summary.kind === 'pending'
				? i18n._(STATS_PENDING_DESCRIPTOR, {count: summary.pending})
				: i18n._(STATS_EMPTY_DESCRIPTOR);
	const badge =
		summary.tuning === 'selective'
			? {label: i18n._(SELECTIVE_DESCRIPTOR), reason: i18n._(SELECTIVE_REASON_DESCRIPTOR)}
			: summary.tuning === 'strict'
				? {label: i18n._(STRICT_DESCRIPTOR), reason: i18n._(STRICT_REASON_DESCRIPTOR)}
				: null;
	return (
		<p className={styles.stats} data-flx={`user.konvo-assistant-tab.proactive.stats.${kind}`}>
			<span className={styles.statsText}>{text}</span>
			{badge && (
				<Tooltip text={badge.reason}>
					<span className={styles.tuning} data-flx={`user.konvo-assistant-tab.proactive.tuning.${summary.tuning}`}>
						{badge.label}
						<span className={styles.srOnly}>. {badge.reason}</span>
					</span>
				</Tooltip>
			)}
		</p>
	);
}

function FeedbackExport() {
	const {i18n} = useLingui();
	const [busy, setBusy] = useState(false);
	const download = () => {
		setBusy(true);
		void KonvoConnections.fetchProactiveFeedbackExport('bad')
			.then((json) => downloadTextFile(json, proactiveFeedbackFileName(new Date()), 'application/json'))
			.catch(() => ToastCommands.error(i18n._(EXPORT_FAILED_DESCRIPTOR)))
			.finally(() => setBusy(false));
	};
	return (
		<div className={styles.group} data-flx="user.konvo-assistant-tab.proactive.feedback">
			<SettingsLinkRow
				icon={<DownloadSimpleIcon size={20} />}
				title={<Trans>Download unwanted briefs</Trans>}
				description={
					<Trans>
						For improving Konvo: a file with the briefs you marked unwanted, what Konvo read, and what it decided. Stays
						on your device until you share it.
					</Trans>
				}
				trailing={<DownloadSimpleIcon size={18} />}
				disabled={busy}
				onClick={download}
				data-flx="user.konvo-assistant-tab.proactive.feedback.download"
			/>
		</div>
	);
}

function TodayLine({today}: {today: {items: number; decisions: number; max_items: number; max_decisions: number}}) {
	const {items, decisions, max_items: maxItems, max_decisions: maxDecisions} = today;
	return (
		<p className={styles.today} data-flx="user.konvo-assistant-tab.proactive.today">
			<Trans>
				Today: {items} of {maxItems} things surfaced · {decisions} of {maxDecisions} checks
			</Trans>
		</p>
	);
}
